import { useState } from "react";

import moment from "moment";

import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { ClientSettingForm } from "@features/mmm/components/explore-budgets/client-setting/form/client-setting-form";
import {
  type ClientSetting,
  type EmptyClientSetting,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { MixesSection } from "@features/mmm/components/explore-budgets/mixes-section";
import { SettingsSection } from "@features/mmm/components/explore-budgets/settings-section";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchHistoricalMixForCycle,
  type FetchHistoricalMixForCycleVariables,
} from "@north-beam/nb-client-side";
import { Modal } from "@shared/modal";

import { ExploreBudgetsTrackedComponent } from "../amplitude-tracking/components";
import { MMMTab } from "../amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "../amplitude-tracking/use-track-mmm-event";
import { useSelectedCycle } from "../hooks/use-selected-cycle";
import { useSelectedCyclePreviousPeriod } from "../hooks/use-selected-cycle-previous-period";

import { HISTORICAL_MIXES_FOR_CYCLE_QUERY } from "./explore-budgets-queries";

export function ExploreBudgetsTab() {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { user } = useUser();
  const { selectedCycle } = useSelectedCycle();
  const previousPeriodDateRange = useSelectedCyclePreviousPeriod();

  const [isShowingClientSettingForm, setIsShowingClientSettingForm] =
    useState<boolean>(false);
  const [clientSettingForForm, setClientSettingForForm] = useState<
    ClientSetting | EmptyClientSetting | null
  >(null);

  const { data: historicalSpendData, loading: historicalSpendDataLoading } =
    useNorthbeamQuery<
      FetchHistoricalMixForCycle,
      FetchHistoricalMixForCycleVariables
    >(HISTORICAL_MIXES_FOR_CYCLE_QUERY, {
      variables: {
        id: selectedCycle.id,
        dateRange: previousPeriodDateRange,
      },
    });

  const onCreateNewClientSetting = () => {
    if (!selectedCycle) {
      // TODO: Handle gracefully
      throw new Error("Client cycle not selected");
    }

    trackMMMEvent({
      component: ExploreBudgetsTrackedComponent.CLIENT_SETTING_FORM,
      action: "onOpen",
      actionInfo: "Create new setting",
    });

    const emptyClientSetting: EmptyClientSetting = {
      client_setting_date_range: {
        id: selectedCycle.date_range.id,
        start_date: selectedCycle.date_range.start_date,
        end_date: selectedCycle.date_range.end_date,
      },
      client_id: user.displayUserId,
      spend_constraints: [],
      createBudgetRecommendationRunResults: [],
      // mix_validations: [],
      name: "",
      budget_low: null,
      budget_high: null,
      kpi: null,
      kpi_v2: null,
      max_cost_per_unit_kpi: null,
      target_spend: null,
      profit_value_per_unit_kpi: null,
      locked: false,
      cycle_id: selectedCycle.id,
    };

    setClientSettingForForm(emptyClientSetting);
    setIsShowingClientSettingForm(true);
  };

  const onEditClientSetting = (clientSettingToEdit: ClientSetting) => {
    trackMMMEvent({
      component: ExploreBudgetsTrackedComponent.CLIENT_SETTING_FORM,
      action: "onOpen",
      actionInfo: "Edit setting",
    });

    setClientSettingForForm(clientSettingToEdit);
    setIsShowingClientSettingForm(true);
  };

  const onModalClose = () => {
    trackMMMEvent({
      component: ExploreBudgetsTrackedComponent.CLIENT_SETTING_FORM,
      action: "onClose",
    });

    setClientSettingForForm(null);
    setIsShowingClientSettingForm(false);
  };

  const renderModal = () => {
    const titleText = clientSettingForForm?.id
      ? "Update Setting"
      : `New Setting for ${moment(
          clientSettingForForm?.client_setting_date_range.start_date,
        ).format("MMM DD, YYYY")} to ${moment(
          clientSettingForForm?.client_setting_date_range.end_date,
        ).format("MMM DD, YYYY")}`;
    const title = (
      <span className="px-4 text-xl font-bold text-v2-primary">
        {titleText}
      </span>
    );
    return (
      <Modal
        size="xl"
        isOpen={isShowingClientSettingForm}
        onClose={onModalClose}
        titleContent={<span>{title}</span>}
        headerProps={{ className: "pl-1.5 pr-6 bg-v2-primary" }}
        bodyClassName="px-0 py-0"
      >
        {clientSettingForForm && historicalSpendDataLoading && (
          <LoadingSlide className="w-[400px] py-32" />
        )}
        {clientSettingForForm && !historicalSpendDataLoading && (
          <ClientSettingForm
            clientSetting={clientSettingForForm}
            historicalSpendData={historicalSpendData}
            onExit={onModalClose}
          />
        )}
      </Modal>
    );
  };

  return (
    <>
      <SettingsSection
        clientCycle={selectedCycle}
        onCreateNewClientSetting={onCreateNewClientSetting}
        onEditClientSetting={onEditClientSetting}
      />
      <MixesSection />
      {renderModal()}
    </>
  );
}
